import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getDatabase, ref, onValue, push, set, update, onDisconnect } from 'firebase/database';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalQueries: 0,
    onlineUsers: 0,
    pendingQueries: 0
  });
  const [queries, setQueries] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');

  useEffect(() => {
    // Check if user is admin
    const userRole = localStorage.getItem('userRole');
    if (userRole !== 'admin') {
      toast.error('Access denied. Admin only.');
      window.location.href = '/';
      return;
    }

    const database = getDatabase();
    const adminId = localStorage.getItem('userId');
    const adminName = localStorage.getItem('userName');

    // Set admin as online
    const adminOnlineRef = ref(database, 'adminOnline');
    set(adminOnlineRef, {
      name: adminName,
      lastSeen: new Date().toISOString()
    });

    // Remove from online when disconnected
    onDisconnect(adminOnlineRef).remove();

    // Load real-time data
    loadStats();
    loadQueries();
    loadOnlineUsers();
  }, []);

  const loadStats = () => {
    try {
      const database = getDatabase();
      const usersRef = ref(database, 'users');
      const queriesRef = ref(database, 'queries');
      const onlineRef = ref(database, 'onlineUsers');

      onValue(usersRef, (snapshot) => {
        const users = snapshot.val();
        setStats(prev => ({ ...prev, totalUsers: users ? Object.keys(users).length : 0 }));
      });

      onValue(queriesRef, (snapshot) => {
        const queries = snapshot.val();
        const queryList = queries ? Object.entries(queries).map(([id, data]) => ({ id, ...data })) : [];
        const pending = queryList.filter(q => q.status === 'pending').length;
        setStats(prev => ({ ...prev, totalQueries: queryList.length, pendingQueries: pending }));
      });

      onValue(onlineRef, (snapshot) => {
        const online = snapshot.val();
        setStats(prev => ({ ...prev, onlineUsers: online ? Object.keys(online).length : 0 }));
      });

      setLoading(false);
    } catch (error) {
      console.error('Error loading stats:', error);
      setLoading(false);
    }
  };

  const loadQueries = () => {
    try {
      const database = getDatabase();
      const queriesRef = ref(database, 'queries');
      onValue(queriesRef, (snapshot) => {
        const queries = snapshot.val();
        const queryList = queries ? Object.entries(queries).map(([id, data]) => ({ id, ...data })) : [];
        setQueries(queryList.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      });
    } catch (error) {
      console.error('Error loading queries:', error);
    }
  };

  const loadOnlineUsers = () => {
    try {
      const database = getDatabase();
      const onlineRef = ref(database, 'onlineUsers');
      onValue(onlineRef, (snapshot) => {
        const online = snapshot.val();
        const userList = online ? Object.entries(online).map(([id, data]) => ({ id, ...data })) : [];
        setOnlineUsers(userList);
      });
    } catch (error) {
      console.error('Error loading online users:', error);
    }
  };

  const handleResolveQuery = async (queryId) => {
    try {
      const database = getDatabase();
      await update(ref(database, `queries/${queryId}`), { status: 'resolved' });
      toast.success('Query marked as resolved');
    } catch (error) {
      toast.error('Failed to resolve query');
    }
  };

  const startChat = (userId, userName) => {
    setSelectedChat({ userId, userName });
    loadChatMessages(userId);
  };

  const loadChatMessages = (userId) => {
    try {
      const database = getDatabase();
      const chatRef = ref(database, `chats/${userId}`);
      onValue(chatRef, (snapshot) => {
        const messages = snapshot.val();
        const messageList = messages ? Object.entries(messages).map(([id, data]) => ({ id, ...data })) : [];
        setChatMessages(messageList.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)));
      });
    } catch (error) {
      console.error('Error loading chat messages:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedChat) return;

    try {
      const database = getDatabase();
      const adminId = localStorage.getItem('userId');
      const adminName = localStorage.getItem('userName');
      
      await push(ref(database, `chats/${selectedChat.userId}`), {
        senderId: adminId,
        senderName: adminName,
        message: newMessage,
        timestamp: new Date().toISOString(),
        isAdmin: true
      });
      
      setNewMessage('');
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    toast.success('Logged out successfully');
    window.location.href = '/';
  };

  const renderContent = () => {
    switch(activeTab) {
      case 'overview':
        return (
          <div>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '24px' }}>Dashboard Overview</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '32px' }}>
              <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>Total Users</div>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#3B82F6' }}>{stats.totalUsers}</div>
              </div>
              <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>Total Queries</div>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#06B6D4' }}>{stats.totalQueries}</div>
              </div>
              <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>Online Users</div>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#10B981' }}>{stats.onlineUsers}</div>
              </div>
              <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>Pending Queries</div>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#dc2626' }}>{stats.pendingQueries}</div>
              </div>
            </div>

            <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px' }}>Recent Queries</h3>
              {queries.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {queries.slice(0, 5).map((query) => (
                    <div key={query.id} style={{ padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontWeight: 600, color: '#1F2937' }}>{query.userName}</span>
                        <span style={{ fontSize: '12px', color: '#6B7280' }}>{new Date(query.createdAt).toLocaleDateString()}</span>
                      </div>
                      
                      {/* Contact Information */}
                      {(query.email || query.phone) && (
                        <div style={{ marginBottom: '8px', fontSize: '12px', color: '#4B5563' }}>
                          {query.email && <span>📧 {query.email} </span>}
                          {query.phone && <span>📞 {query.phone}</span>}
                        </div>
                      )}
                      
                      <p style={{ fontSize: '14px', color: '#4B5563', marginBottom: '12px' }}>
                        {query.initialMessage && <strong>Initial: </strong>}{query.initialMessage}
                        {query.query && <div><strong>Query: </strong>{query.query}</div>}
                        {!query.initialMessage && !query.query && query.message}
                      </p>
                      
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <span style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 600, backgroundColor: query.status === 'pending' ? '#FEF3C7' : '#D1FAE5', color: query.status === 'pending' ? '#92400E' : '#065F46' }}>
                          {query.status}
                        </span>
                        {query.awaitingAdmin && (
                          <span style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 600, backgroundColor: '#DBEAFE', color: '#1E40AF' }}>
                            Awaiting Admin
                          </span>
                        )}
                        {query.status === 'pending' && (
                          <button
                            onClick={() => handleResolveQuery(query.id)}
                            style={{ padding: '4px 12px', borderRadius: '6px', fontSize: '12px', backgroundColor: '#3B82F6', color: 'white', border: 'none', cursor: 'pointer' }}
                          >
                            Mark Resolved
                          </button>
                        )}
                        {query.userId && (
                          <button
                            onClick={() => { setActiveTab('liveChat'); startChat(query.userId, query.userName); }}
                            style={{ padding: '4px 12px', borderRadius: '6px', fontSize: '12px', backgroundColor: '#10B981', color: 'white', border: 'none', cursor: 'pointer' }}
                          >
                            Join Chat
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#6B7280' }}>No queries yet.</p>
              )}
            </div>
          </div>
        );

      case 'queries':
        return (
          <div>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '24px' }}>User Queries</h2>
            <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              {queries.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {queries.map((query) => (
                    <div key={query.id} style={{ padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontWeight: 600, color: '#1F2937' }}>{query.userName}</span>
                        <span style={{ fontSize: '12px', color: '#6B7280' }}>{new Date(query.createdAt).toLocaleString()}</span>
                      </div>
                      
                      {/* Contact Information */}
                      {(query.email || query.phone) && (
                        <div style={{ marginBottom: '12px', fontSize: '13px', color: '#4B5563' }}>
                          {query.email && <div>📧 {query.email}</div>}
                          {query.phone && <div>📞 {query.phone}</div>}
                        </div>
                      )}
                      
                      <p style={{ fontSize: '14px', color: '#4B5563', marginBottom: '12px' }}>
                        {query.initialMessage && <strong>Initial: </strong>}{query.initialMessage}
                        {query.query && <div><strong>Query: </strong>{query.query}</div>}
                        {!query.initialMessage && !query.query && query.message}
                      </p>
                      
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <span style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 600, backgroundColor: query.status === 'pending' ? '#FEF3C7' : '#D1FAE5', color: query.status === 'pending' ? '#92400E' : '#065F46' }}>
                          {query.status}
                        </span>
                        {query.awaitingAdmin && (
                          <span style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 600, backgroundColor: '#DBEAFE', color: '#1E40AF' }}>
                            Awaiting Admin
                          </span>
                        )}
                        {query.status === 'pending' && (
                          <button
                            onClick={() => handleResolveQuery(query.id)}
                            style={{ padding: '4px 12px', borderRadius: '6px', fontSize: '12px', backgroundColor: '#3B82F6', color: 'white', border: 'none', cursor: 'pointer' }}
                          >
                            Mark Resolved
                          </button>
                        )}
                        {query.userId && (
                          <button
                            onClick={() => { setActiveTab('liveChat'); startChat(query.userId, query.userName); }}
                            style={{ padding: '4px 12px', borderRadius: '6px', fontSize: '12px', backgroundColor: '#10B981', color: 'white', border: 'none', cursor: 'pointer' }}
                          >
                            Join Chat
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#6B7280' }}>No queries yet.</p>
              )}
            </div>
          </div>
        );

      case 'liveChat':
        return (
          <div>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '24px' }}>Live Chat</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '20px', height: '600px' }}>
              {/* Online Users List */}
              <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', overflowY: 'auto' }}>
                <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '16px' }}>Online Users ({onlineUsers.length})</h3>
                {onlineUsers.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {onlineUsers.map((user) => (
                      <div
                        key={user.id}
                        onClick={() => startChat(user.id, user.name)}
                        style={{
                          padding: '12px',
                          backgroundColor: selectedChat?.userId === user.id ? '#3B82F6' : '#f9fafb',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          border: '1px solid #e5e7eb'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#10B981' }}></div>
                          <span style={{ fontWeight: 500, color: selectedChat?.userId === user.id ? 'white' : '#1F2937' }}>{user.name}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: '#6B7280', fontSize: '14px' }}>No users online</p>
                )}
              </div>

              {/* Chat Window */}
              <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column' }}>
                {selectedChat ? (
                  <>
                    <div style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '12px', marginBottom: '12px' }}>
                      <h3 style={{ fontSize: '16px', fontWeight: 'bold' }}>Chat with {selectedChat.userName}</h3>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {chatMessages.length > 0 ? (
                        chatMessages.map((msg) => (
                          <div
                            key={msg.id}
                            style={{
                              maxWidth: '70%',
                              padding: '12px',
                              borderRadius: '12px',
                              backgroundColor: msg.isAdmin ? '#3B82F6' : '#f3f4f6',
                              color: msg.isAdmin ? 'white' : '#1F2937',
                              alignSelf: msg.isAdmin ? 'flex-end' : 'flex-start'
                            }}
                          >
                            <div style={{ fontSize: '12px', marginBottom: '4px', opacity: 0.8 }}>{msg.senderName}</div>
                            <div style={{ fontSize: '14px' }}>{msg.message}</div>
                          </div>
                        ))
                      ) : (
                        <p style={{ color: '#6B7280', textAlign: 'center' }}>No messages yet</p>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type your message..."
                        onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                        style={{
                          flex: 1,
                          padding: '12px',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          outline: 'none'
                        }}
                      />
                      <button
                        onClick={sendMessage}
                        style={{
                          padding: '12px 24px',
                          backgroundColor: '#3B82F6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: 600
                        }}
                      >
                        Send
                      </button>
                    </div>
                  </>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#6B7280' }}>
                    Select a user to start chatting
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'settings':
        return (
          <div>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '24px' }}>Settings</h2>
            <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px' }}>Company Information</h3>
                <p style={{ color: '#6B7280' }}>
                  Company: {localStorage.getItem('companyName') || 'Not set'}
                </p>
              </div>
              <button
                onClick={handleLogout}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Logout
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: '18px', color: '#6B7280' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f3f4f6' }}>
      <ToastContainer />
      <div style={{ display: 'flex' }}>
        {/* Sidebar */}
        <div style={{ 
          width: '250px', 
          backgroundColor: '#1e293b', 
          minHeight: '100vh',
          padding: '24px',
          position: 'fixed',
          left: 0,
          top: 0
        }}>
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: 'white', marginBottom: '8px' }}>
              Travel Immigration
            </h2>
            <p style={{ fontSize: '12px', color: '#94a3b8' }}>
              {localStorage.getItem('companyName') || 'Company Admin'}
            </p>
          </div>

          <nav style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {[
              { id: 'overview', label: 'Overview', icon: '📊' },
              { id: 'queries', label: 'User Queries', icon: '💬' },
              { id: 'liveChat', label: 'Live Chat', icon: '💻' },
              { id: 'settings', label: 'Settings', icon: '⚙️' }
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                style={{
                  padding: '12px 16px',
                  backgroundColor: activeTab === item.id ? '#dc2626' : 'transparent',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: '14px',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}
              >
                <span>{item.icon}</span>
                {item.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Main Content */}
        <div style={{ marginLeft: '250px', flex: 1, padding: '32px' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
